import { FormEvent, useEffect, useMemo, useState } from "react"
import { MunicipioIbgeSelect } from "../../components/app/MunicipioIbgeSelect"
import { Tabs } from "../../components/app/Tabs"
import { apiFetch } from "../../lib/api"
import { useEmpresa } from "../../app/context/EmpresaContext"

const GESTAO_TABS = [
  { id: "emitente", label: "Emitente" },
  { id: "empresas", label: "Empresas" },
  { id: "usuarios", label: "Usuários" },
  { id: "apikeys", label: "API Keys" },
  { id: "empresa", label: "Config. empresa" },
  { id: "organizacao", label: "Config. organização" },
]

const UF_OPTIONS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
]

const onlyDigits = (value: string) => value.replace(/\D/g, "")

type EmitenteForm = {
  nome_fantasia: string
  inscricao_estadual: string
  uf: string
  codigo_municipio: string
  municipio: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cep: string
  telefone: string
  regime_tributario: string
  cnpj: string
  razao_social: string
  campos_ausentes: string[]
}

const parseApiError = (err: unknown, fallback = "Operação falhou") => {
  if (!(err instanceof Error)) return fallback
  try {
    const parsed = JSON.parse(err.message) as { detail?: string }
    if (typeof parsed.detail === "string") return parsed.detail
  } catch {
    /* texto bruto */
  }
  return err.message || fallback
}

export const GestaoPage = () => {
  const { me, empresas, empresaId, loading: ctxLoading } = useEmpresa()

  const orgId = useMemo(() => {
    const fromEmpresaAtiva = empresas.find((e) => e.id === empresaId)?.organizacao_id
    if (fromEmpresaAtiva) return fromEmpresaAtiva
    if (empresas[0]?.organizacao_id) return empresas[0].organizacao_id
    return me?.organizacao_ids?.[0] ?? ""
  }, [empresas, empresaId, me?.organizacao_ids])

  const [tab, setTab] = useState("emitente")
  const [orgEmpresas, setOrgEmpresas] = useState<Array<{ id: string; razao_social: string; cnpj: string; ativo?: boolean }>>([])
  const [memberships, setMemberships] = useState<Array<{ id: string; email: string; papel: string }>>([])
  const [apiKeys, setApiKeys] = useState<Array<{ id: string; nome: string; prefix: string }>>([])
  const [empresaConfig, setEmpresaConfig] = useState("{}")
  const [orgConfig, setOrgConfig] = useState("{}")
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [loadingOrg, setLoadingOrg] = useState(false)
  const [loadingEmpresa, setLoadingEmpresa] = useState(false)
  const [newKeySecret, setNewKeySecret] = useState("")
  const [emitente, setEmitente] = useState<EmitenteForm | null>(null)
  const [savingEmitente, setSavingEmitente] = useState(false)

  const loadOrgData = async (oid: string) => {
    setLoadingOrg(true)
    setError("")
    try {
      const [empresasOrg, members, cfg] = await Promise.all([
        apiFetch<Array<{ id: string; razao_social: string; cnpj: string; ativo?: boolean }>>(
          `/v1/gestao/organizacoes/${oid}/empresas`,
        ),
        apiFetch<Array<{ id: string; email: string; papel: string }>>(`/v1/gestao/organizacoes/${oid}/memberships`),
        apiFetch<{ config: Record<string, unknown> }>(`/v1/gestao/organizacoes/${oid}/config`),
      ])
      setOrgEmpresas(empresasOrg)
      setMemberships(members)
      setOrgConfig(JSON.stringify(cfg.config ?? {}, null, 2))
    } catch (err) {
      setOrgEmpresas([])
      setMemberships([])
      setError(parseApiError(err, "Não foi possível carregar dados da organização"))
    } finally {
      setLoadingOrg(false)
    }
  }

  const loadEmpresaData = async (eid: string) => {
    setLoadingEmpresa(true)
    setEmitente(null)
    setError("")
    try {
      const [keys, cfg, cadastro] = await Promise.all([
        apiFetch<Array<{ id: string; nome: string; prefix: string }>>(`/v1/gestao/empresas/${eid}/api-keys`),
        apiFetch<{ config: Record<string, unknown> }>(`/v1/gestao/empresas/${eid}/config`),
        apiFetch<{
          cnpj: string
          razao_social: string
          nome_fantasia: string
          inscricao_estadual: string
          uf: string
          codigo_municipio: string
          municipio: string
          logradouro: string
          numero: string
          complemento: string
          bairro: string
          cep: string
          telefone: string
          crt: number
          campos_ausentes: string[]
        }>(`/v1/gestao/empresas/${eid}/emitente`),
      ])
      setApiKeys(keys)
      setEmpresaConfig(JSON.stringify(cfg.config ?? {}, null, 2))
      setEmitente({
        cnpj: cadastro.cnpj,
        razao_social: cadastro.razao_social,
        nome_fantasia: cadastro.nome_fantasia,
        inscricao_estadual: cadastro.inscricao_estadual,
        uf: cadastro.uf,
        codigo_municipio: cadastro.codigo_municipio,
        municipio: cadastro.municipio,
        logradouro: cadastro.logradouro,
        numero: cadastro.numero,
        complemento: cadastro.complemento,
        bairro: cadastro.bairro,
        cep: cadastro.cep,
        telefone: cadastro.telefone,
        regime_tributario: String(cadastro.crt || 1),
        campos_ausentes: cadastro.campos_ausentes,
      })
    } catch (err) {
      setApiKeys([])
      setError(parseApiError(err, "Não foi possível carregar dados da empresa"))
    } finally {
      setLoadingEmpresa(false)
    }
  }

  useEffect(() => {
    if (ctxLoading || !orgId) return
    loadOrgData(orgId)
  }, [orgId, ctxLoading])

  useEffect(() => {
    if (ctxLoading || !empresaId) return
    loadEmpresaData(empresaId)
  }, [empresaId, ctxLoading])

  const empresaAtiva = empresas.find((empresa) => empresa.id === empresaId)

  const handleCopyEmpresaId = async () => {
    if (!empresaId) return
    setError("")
    try {
      await navigator.clipboard.writeText(empresaId)
      setMessage("UUID da empresa copiado.")
    } catch {
      setError("Não foi possível copiar o UUID. Selecione o código e copie manualmente.")
    }
  }

  const handleNewKey = async (e: FormEvent) => {
    e.preventDefault()
    if (!empresaId) return
    setMessage("")
    setError("")
    try {
      const res = await apiFetch<{ api_key: string }>(`/v1/gestao/empresas/${empresaId}/api-keys`, {
        method: "POST",
        body: JSON.stringify({ nome: "Integração ERP", escopos: ["nfe:emitir", "nfe:consultar"] }),
      })
      setNewKeySecret(res.api_key)
      setMessage("API key criada — copie o valor abaixo (não será exibido de novo).")
      await loadEmpresaData(empresaId)
    } catch (err) {
      setError(parseApiError(err, "Falha ao criar API key"))
    }
  }

  const handleNewMember = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!orgId) return
    setMessage("")
    setError("")
    const fd = new FormData(e.currentTarget)
    try {
      await apiFetch(`/v1/gestao/organizacoes/${orgId}/memberships`, {
        method: "POST",
        body: JSON.stringify({
          email: String(fd.get("email")),
          papel: String(fd.get("papel")),
          empresa_id: fd.get("empresa_id") || null,
        }),
      })
      setMessage("Usuário vinculado")
      e.currentTarget.reset()
      await loadOrgData(orgId)
    } catch (err) {
      setError(parseApiError(err, "Falha ao vincular usuário"))
    }
  }

  if (ctxLoading) {
    return <p className="text-sm text-slate-600">Carregando contexto…</p>
  }

  if (!me?.is_gestor && !me?.is_super_admin) {
    return <p className="text-sm text-slate-600">Acesso restrito a administradores de organização ou empresa.</p>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Gestão</h1>
        <p className="text-sm text-slate-600">
          Administração da organização{" "}
          {orgId ? (
            <span className="font-mono text-xs text-slate-500">({orgId.slice(0, 8)}…)</span>
          ) : (
            <span className="text-amber-700">— selecione uma empresa ativa para identificar a organização</span>
          )}{" "}
          e da empresa ativa.
        </p>
      </div>

      {!orgId && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Nenhuma organização identificada. Selecione uma <strong>empresa ativa</strong> no menu lateral (cada empresa
          pertence a uma organização).
        </p>
      )}

      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="alert">
          {error}
        </p>
      ) : null}

      {message ? <p className="text-sm text-emerald-700">{message}</p> : null}

      <Tabs tabs={GESTAO_TABS} active={tab} onChange={setTab} />

      {tab === "emitente" && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          {!empresaId ? (
            <p className="text-sm text-slate-600">Selecione uma empresa ativa no menu lateral.</p>
          ) : loadingEmpresa || !emitente ? (
            <p className="text-sm text-slate-500">Carregando cadastro do emitente…</p>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                setError("")
                setMessage("")
                const codigoMunicipio = onlyDigits(emitente.codigo_municipio)
                const cep = onlyDigits(emitente.cep)
                if (codigoMunicipio.length !== 7) {
                  setError("Código IBGE do município deve ter 7 dígitos.")
                  return
                }
                if (!emitente.municipio.trim()) {
                  setError("Informe o município.")
                  return
                }
                if (cep.length !== 8) {
                  setError("CEP deve ter 8 dígitos.")
                  return
                }
                setSavingEmitente(true)
                try {
                  const saved = await apiFetch<EmitenteForm & { crt: number; campos_ausentes: string[] }>(
                    `/v1/gestao/empresas/${empresaId}/emitente`,
                    {
                      method: "PUT",
                      body: JSON.stringify({
                        nome_fantasia: emitente.nome_fantasia.trim() || null,
                        inscricao_estadual: emitente.inscricao_estadual.trim(),
                        uf: emitente.uf,
                        codigo_municipio: codigoMunicipio,
                        municipio: emitente.municipio.trim(),
                        logradouro: emitente.logradouro.trim(),
                        numero: emitente.numero.trim(),
                        complemento: emitente.complemento.trim(),
                        bairro: emitente.bairro.trim(),
                        cep,
                        telefone: onlyDigits(emitente.telefone),
                        regime_tributario: Number(emitente.regime_tributario),
                      }),
                    },
                  )
                  setEmitente({
                    ...emitente,
                    nome_fantasia: saved.nome_fantasia,
                    inscricao_estadual: saved.inscricao_estadual,
                    uf: saved.uf,
                    codigo_municipio: saved.codigo_municipio,
                    municipio: saved.municipio,
                    logradouro: saved.logradouro,
                    numero: saved.numero,
                    complemento: saved.complemento,
                    bairro: saved.bairro,
                    cep: saved.cep,
                    telefone: saved.telefone,
                    regime_tributario: String(saved.crt || emitente.regime_tributario),
                    campos_ausentes: saved.campos_ausentes,
                  })
                  setMessage("Cadastro do emitente salvo. O GET /emitente passa a devolver estes dados.")
                } catch (err) {
                  setError(parseApiError(err, "Falha ao salvar o emitente"))
                } finally {
                  setSavingEmitente(false)
                }
              }}
              className="space-y-4"
            >
              <div>
                <h2 className="font-semibold text-slate-900">Dados do emitente</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Estes campos entram no XML da NF-e. CNPJ e razão social permanecem os do cadastro da empresa.
                </p>
              </div>
              {emitente.campos_ausentes.length > 0 && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950" role="status">
                  Falta completar: {emitente.campos_ausentes.join(", ")}. Sem isso a API do emitente responde 422.
                </p>
              )}
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">CNPJ</span>
                  <input value={emitente.cnpj} readOnly className="rounded-lg border bg-slate-50 px-3 py-2 text-sm" aria-label="CNPJ" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Razão social</span>
                  <input value={emitente.razao_social} readOnly className="rounded-lg border bg-slate-50 px-3 py-2 text-sm" aria-label="Razão social" />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Nome fantasia</span>
                  <input
                    value={emitente.nome_fantasia}
                    onChange={(e) => setEmitente({ ...emitente, nome_fantasia: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Nome fantasia"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Inscrição estadual</span>
                  <input
                    required
                    value={emitente.inscricao_estadual}
                    onChange={(e) => setEmitente({ ...emitente, inscricao_estadual: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Inscrição estadual"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Regime tributário (CRT)</span>
                  <select
                    value={emitente.regime_tributario}
                    onChange={(e) => setEmitente({ ...emitente, regime_tributario: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Regime tributário"
                  >
                    <option value="1">1 — Simples Nacional</option>
                    <option value="2">2 — Simples (excesso sublimite)</option>
                    <option value="3">3 — Regime normal</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">UF</span>
                  <select
                    value={emitente.uf}
                    onChange={(e) => setEmitente({ ...emitente, uf: e.target.value, codigo_municipio: "", municipio: "" })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="UF"
                  >
                    {UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>{uf}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Município</span>
                  <MunicipioIbgeSelect
                    uf={emitente.uf}
                    ibge={emitente.codigo_municipio}
                    municipioNome={emitente.municipio}
                    searchPath="/v1/gestao/municipios"
                    onSelect={(code, nome) => setEmitente({ ...emitente, codigo_municipio: code, municipio: nome })}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Logradouro</span>
                  <input
                    required
                    maxLength={60}
                    value={emitente.logradouro}
                    onChange={(e) => setEmitente({ ...emitente, logradouro: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Logradouro"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Número</span>
                  <input
                    required
                    maxLength={60}
                    value={emitente.numero}
                    onChange={(e) => setEmitente({ ...emitente, numero: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Número"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Complemento</span>
                  <input
                    maxLength={60}
                    value={emitente.complemento}
                    onChange={(e) => setEmitente({ ...emitente, complemento: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Complemento"
                    placeholder="Sala, bloco, conjunto"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Bairro</span>
                  <input
                    required
                    maxLength={60}
                    value={emitente.bairro}
                    onChange={(e) => setEmitente({ ...emitente, bairro: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    aria-label="Bairro"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">CEP</span>
                  <input
                    required
                    value={emitente.cep}
                    onChange={(e) => setEmitente({ ...emitente, cep: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    inputMode="numeric"
                    aria-label="CEP"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Telefone</span>
                  <input
                    value={emitente.telefone}
                    onChange={(e) => setEmitente({ ...emitente, telefone: e.target.value })}
                    className="rounded-lg border px-3 py-2 text-sm"
                    inputMode="tel"
                    aria-label="Telefone"
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={savingEmitente}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {savingEmitente ? "Salvando…" : "Salvar emitente"}
              </button>
            </form>
          )}
        </div>
      )}

      {tab === "empresas" && (
        <div className="rounded-xl border border-slate-200 bg-white">
          {loadingOrg ? (
            <p className="px-4 py-6 text-sm text-slate-500">Carregando empresas…</p>
          ) : orgEmpresas.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-600">
              {orgId ? "Nenhuma empresa nesta organização." : "Selecione uma empresa ativa para carregar a lista."}
            </p>
          ) : (
            <ul className="divide-y text-sm">
              {orgEmpresas.map((e) => (
                <li key={e.id} className="px-4 py-3">
                  {e.razao_social} — {e.cnpj}
                  {e.ativo === false && (
                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">Arquivada</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "usuarios" && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white">
            {loadingOrg ? (
              <p className="px-4 py-6 text-sm text-slate-500">Carregando usuários…</p>
            ) : memberships.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-600">Nenhum usuário vinculado nesta organização.</p>
            ) : (
              <ul className="divide-y text-sm">
                {memberships.map((m) => (
                  <li key={m.id} className="px-4 py-3">
                    {m.email} — {m.papel}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {orgId ? (
            <form onSubmit={handleNewMember} className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="font-semibold text-slate-900">Adicionar usuário</h3>
              <input name="email" type="email" required placeholder="E-mail" className="w-full rounded-lg border px-3 py-2 text-sm" />
              <select name="papel" className="w-full rounded-lg border px-3 py-2 text-sm" defaultValue="emissor">
                <option value="emissor">Emissor</option>
                <option value="consulta">Consulta</option>
                <option value="empresa_admin">Admin empresa</option>
                <option value="grupo_admin">Admin grupo</option>
              </select>
              <input name="empresa_id" placeholder="UUID empresa (opcional)" className="w-full rounded-lg border px-3 py-2 text-sm" />
              <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700">
                Vincular
              </button>
            </form>
          ) : null}
        </div>
      )}

      {tab === "apikeys" && (
        <div className="space-y-4">
          {!empresaId ? (
            <p className="text-sm text-slate-600">Selecione uma empresa ativa para gerenciar API keys.</p>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">UUID da empresa</p>
                {empresaAtiva ? (
                  <p className="mt-1 text-sm text-slate-700">{empresaAtiva.nome_fantasia || empresaAtiva.razao_social}</p>
                ) : null}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <code
                    className="break-all rounded bg-slate-100 px-2 py-1 font-mono text-sm text-slate-900"
                    aria-label="UUID da empresa"
                  >
                    {empresaId}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyEmpresaId}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
                    aria-label="Copiar UUID da empresa"
                  >
                    Copiar UUID
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Use este valor nas rotas <span className="font-mono">/v1/empresas/{empresaId}/...</span>
                </p>
              </div>
              <button
                type="button"
                onClick={handleNewKey}
                disabled={loadingEmpresa}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
              >
                Nova API key
              </button>
              {newKeySecret && (
                <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-950">
                  Guarde agora: <code className="break-all">{newKeySecret}</code>
                </p>
              )}
              <div className="rounded-xl border border-slate-200 bg-white">
                {loadingEmpresa ? (
                  <p className="px-4 py-6 text-sm text-slate-500">Carregando chaves…</p>
                ) : apiKeys.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-slate-600">Nenhuma API key cadastrada para esta empresa.</p>
                ) : (
                  <ul className="divide-y text-sm">
                    {apiKeys.map((k) => (
                      <li key={k.id} className="px-4 py-3">
                        {k.nome} — {k.prefix}…
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "empresa" && (
        <>
          {!empresaId ? (
            <p className="text-sm text-slate-600">Selecione uma empresa ativa.</p>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                setError("")
                try {
                  await apiFetch(`/v1/gestao/empresas/${empresaId}/config`, {
                    method: "PUT",
                    body: JSON.stringify({ config: JSON.parse(empresaConfig) }),
                  })
                  setMessage("Config empresa salva")
                } catch (err) {
                  setError(parseApiError(err, "Falha ao salvar config da empresa"))
                }
              }}
              className="space-y-3"
            >
              <textarea
                value={empresaConfig}
                onChange={(e) => setEmpresaConfig(e.target.value)}
                rows={12}
                className="w-full rounded-xl border border-slate-200 bg-white p-3 font-mono text-xs"
              />
              <p className="text-xs text-slate-500">Ex.: {`{"notificacoes_email":"","webhook_padrao":""}`}</p>
              <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700">
                Salvar empresa
              </button>
            </form>
          )}
        </>
      )}

      {tab === "organizacao" && (
        <>
          {!orgId ? (
            <p className="text-sm text-slate-600">Organização não identificada — selecione uma empresa ativa.</p>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                setError("")
                try {
                  await apiFetch(`/v1/gestao/organizacoes/${orgId}/config`, {
                    method: "PUT",
                    body: JSON.stringify({ config: JSON.parse(orgConfig) }),
                  })
                  setMessage("Config organização salva")
                } catch (err) {
                  setError(parseApiError(err, "Falha ao salvar config da organização"))
                }
              }}
              className="space-y-3"
            >
              <textarea
                value={orgConfig}
                onChange={(e) => setOrgConfig(e.target.value)}
                rows={12}
                className="w-full rounded-xl border border-slate-200 bg-white p-3 font-mono text-xs"
              />
              <p className="text-xs text-slate-500">Ex.: {`{"ia_habilitada":false,"nome_exibicao":""}`}</p>
              <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700">
                Salvar organização
              </button>
            </form>
          )}
        </>
      )}
    </div>
  )
}
